// SPDX-License-Identifier: BUSL-1.1

/**
 * GettingStartedChecklist — per-org onboarding checklist.
 *
 * Shown on the dashboard for "new" orgs (those with no agents, no members,
 * no configs, or no feature flags). Each step links to the relevant page so
 * users can complete setup in a few clicks.
 *
 * The checklist is a collapsible card that expands to show all steps.
 * Completed steps are tracked in localStorage keyed by orgId so each org
 * remembers its own progress independently.
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  Bot,
  Users,
  FileCode,
  ToggleLeft,
  KeyIcon,
  Sparkles,
  GitFork,
} from 'lucide-react';
import { apiFetch, shouldRetryApiQuery } from '@/lib/api-client';
import {
  AGENTS_QUERY_KEY,
  CONFIGS_QUERY_KEY,
  FLAGS_QUERY_KEY,
  queryKeyLabel,
} from '@/lib/bootstrap-query-keys';
import { useOrg } from '@/contexts/org-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ChecklistStep {
  id: string;
  label: string;
  description: string;
  href: string;
  icon: React.ElementType;
  /** Always show this step even if its prerequisite data is missing. */
  always?: boolean;
}

const STEPS: ChecklistStep[] = [
  {
    id: 'invite-members',
    label: 'Invite team members',
    description: 'Add colleagues to your organization and assign roles.',
    href: '/members',
    icon: Users,
  },
  {
    id: 'create-agent',
    label: 'Create your first agent',
    description: 'Set up an AI agent with a system prompt and tools.',
    href: '/agents',
    icon: Bot,
  },
  {
    id: 'create-config',
    label: 'Create a config surface',
    description: 'Define configuration that your agents will use at runtime.',
    href: '/configs',
    icon: FileCode,
  },
  {
    id: 'set-up-flags',
    label: 'Configure feature flags',
    description: 'Control agent behaviour with flags you can toggle live.',
    href: '/flags',
    icon: ToggleLeft,
  },
  {
    id: 'create-api-key',
    label: 'Create an API key',
    description: 'Generate a key to integrate external tools with build.ceo.',
    href: '/keys',
    icon: KeyIcon,
  },
  {
    id: 'connect-github',
    label: 'Connect GitHub',
    description: 'Install the build.ceo GitHub App to sync activity into the unified timeline.',
    href: '/github-settings',
    icon: GitFork,
  },
];

const STORAGE_KEY_PREFIX = 'ceo-onboarding-completed-';

/** Returns a Set of completed step IDs for the current org. */
function useCompletedSteps(orgId: string | null): {
  completed: Set<string>;
  markDone: (stepId: string) => void;
} {
  const [completed, setCompleted] = useState<Set<string>>(() => {
    if (!orgId) return new Set();
    try {
      const stored = localStorage.getItem(STORAGE_KEY_PREFIX + orgId);
      return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const markDone = (stepId: string) => {
    setCompleted((prev) => {
      const next = new Set(prev);
      next.add(stepId);
      if (orgId) {
        localStorage.setItem(STORAGE_KEY_PREFIX + orgId, JSON.stringify([...next]));
      }
      return next;
    });
  };

  return { completed, markDone };
}

export function GettingStartedChecklist() {
  const { currentOrg, bootstrapReady } = useOrg();
  const [expanded, setExpanded] = useState(true);

  const { data: agents = [] } = useQuery({
    queryKey: AGENTS_QUERY_KEY,
    enabled: bootstrapReady,
    queryFn: () => apiFetch<unknown[]>('/agents', { _queryKey: queryKeyLabel(AGENTS_QUERY_KEY) }),
    staleTime: 30_000,
    retry: shouldRetryApiQuery,
  });

  const { data: members = [] } = useQuery({
    queryKey: ['members', currentOrg?.id ?? ''],
    enabled: bootstrapReady && !!currentOrg?.id,
    // Note: apiFetch prepends API_BASE, so use /orgs/:id/members not /members
    queryFn: () =>
      currentOrg?.id
        ? apiFetch<unknown[]>(`/orgs/${currentOrg.id}/members`, {
            _queryKey: JSON.stringify(['members', currentOrg.id]),
          })
        : Promise.resolve([] as unknown[]),
    staleTime: 30_000,
    retry: shouldRetryApiQuery,
  });

  const { data: configs = [] } = useQuery({
    queryKey: CONFIGS_QUERY_KEY,
    enabled: bootstrapReady,
    queryFn: () => apiFetch<unknown[]>('/configs', { _queryKey: queryKeyLabel(CONFIGS_QUERY_KEY) }),
    staleTime: 30_000,
    retry: shouldRetryApiQuery,
  });

  const { data: flags = [] } = useQuery({
    queryKey: FLAGS_QUERY_KEY,
    enabled: bootstrapReady,
    queryFn: () => apiFetch<unknown[]>('/flags', { _queryKey: queryKeyLabel(FLAGS_QUERY_KEY) }),
    staleTime: 30_000,
    retry: shouldRetryApiQuery,
  });

  const { data: githubStatus } = useQuery({
    queryKey: ['github-installation', currentOrg?.slug ?? ''],
    enabled: bootstrapReady && !!currentOrg?.slug,
    queryFn: () =>
      currentOrg?.slug
        ? // githubAuth router mounted at /api/auth with route /orgs/:slug/github/installation
          // noRedirect: true prevents 401 from logging out the user
          apiFetch<{ installed: boolean }>(`/auth/orgs/${currentOrg.slug}/github/installation`, {
            noRedirect: true,
            _queryKey: JSON.stringify(['github-installation', currentOrg.slug]),
          })
        : Promise.resolve({ installed: false }),
    staleTime: 60_000,
    retry: shouldRetryApiQuery,
  });

  // Always call hooks unconditionally — React Hooks rules require this before any early returns
  const { completed, markDone } = useCompletedSteps(currentOrg?.id ?? null);

  const isNewOrg =
    agents.length === 0 || members.length === 0 || configs.length === 0 || flags.length === 0;

  const githubInstalled = githubStatus?.installed === true;

  // Show checklist if org has no resources yet OR GitHub hasn't been connected
  if (!isNewOrg && githubInstalled) return null;
  const completedCount = completed.size;
  const totalCount = STEPS.length;
  const allDone = completedCount === totalCount;

  return (
    <Card className="border-emerald-900/40 bg-emerald-950/20">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-emerald-400" />
          <CardTitle className="text-sm font-semibold text-emerald-300">Getting started</CardTitle>
          <span className="rounded bg-emerald-900/50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-400">
            {completedCount}/{totalCount}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 text-neutral-400 hover:text-neutral-200"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          {expanded ? 'Collapse' : 'Expand'}
        </Button>
      </CardHeader>

      {expanded && (
        <CardContent className="grid gap-2 pt-0">
          {STEPS.map((step) => {
            const isDone = completed.has(step.id);
            return (
              <div
                key={step.id}
                className={cn(
                  'flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 transition-colors',
                  isDone
                    ? 'border-emerald-900/30 bg-emerald-950/30'
                    : 'hover:border-neutral-800 hover:bg-neutral-900/50',
                )}
              >
                <button
                  onClick={() => !isDone && markDone(step.id)}
                  className="flex-shrink-0 transition-colors"
                  aria-label={isDone ? 'Completed' : 'Mark as done'}
                >
                  {isDone ? (
                    <CheckCircle2 className="size-4 text-emerald-400" />
                  ) : (
                    <Circle className="size-4 text-neutral-600 hover:text-emerald-500" />
                  )}
                </button>

                <div className="flex min-w-0 flex-1 flex-col">
                  <span
                    className={cn(
                      'text-sm',
                      isDone ? 'text-emerald-400 line-through' : 'text-neutral-200',
                    )}
                  >
                    {step.label}
                  </span>
                  <span className="text-xs text-neutral-500">{step.description}</span>
                </div>

                <Link
                  to={step.href}
                  className={cn(
                    'flex-shrink-0 rounded px-2 py-1 text-xs font-medium transition-colors',
                    isDone
                      ? 'bg-emerald-900/40 text-emerald-500'
                      : 'bg-emerald-700/60 text-emerald-200 hover:bg-emerald-600/60',
                  )}
                >
                  {isDone ? 'View' : 'Go'}
                </Link>
              </div>
            );
          })}

          {allDone && (
            <p className="mt-1 text-center text-xs text-emerald-500">
              ✨ Your organization is set up! Remove this checklist from your dashboard by adding
              your first resource.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
